/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  List,
  ListSubheader,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'react-router-dom';

const sections = [
  {
    subheader: ' ',
    items: [
      {
        title: 'dashboard',
        icon: 'AreaChart',
        href: 'dashboard'
      },
      {
        title: 'workers',
        icon: 'Server',
        href: 'workers'
      },
      {
        title: 'dailyStatistics',
        icon: 'BarChart3',
        href: 'daily-hashrate'
      }
    ]
  }
];

function renderNavItems({
  items,
  pathname,
  depth = 0,
  translation
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth, translation }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
  translation
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={translation(item.title)}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href + window.location.search}
        icon={item.icon}
        info={item.info}
        key={key}
        title={translation(item.title)}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles()((theme) => ({
  mobileDrawer: {
    width: 230
  },
  desktopDrawer: {
    width: 230,
    top: 74,
    height: 'calc(100% - 74px)',
    zIndex: 1000
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
   logoSmall: {
    width: '75%'
  },
  metalMenu: {
    width: 230,
    height: '100%',
    zIndex: 1000,
    backgroundColor:" var(--uishell-color-surface)",
  },
  autoCompleteElem: {
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    width: theme.spacing(20),
  },
  metalBackground: {
    backgroundColor: 'var(--neutral-1)'
  },
}));

const NavBar = (props) => {
  
  const { onMobileClose, openMobile } = props
  const query = new URLSearchParams(window.location.search);

  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const location = useLocation();
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const isFirefox = typeof InstallTrigger !== 'undefined';

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        {
          mobileDevice && 
          <Box
            p={2}
            style={{borderBottom: "1px solid #303432",}} 
            display="flex"
            justifyContent="center"
          >
            <RouterLink to={{ pathname: "dashboard", search: window.location.search }} style={isFirefox ? {width: "100%"} : {}}>
              <Logo className={classes.logoSmall}/>
            </RouterLink>
          </Box>
        }        
        <Box style={{display: "flex", flexDirection: "column", height: "calc(100% - 74px)", alignItems: "center", paddingBottom: "16px"}}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {section.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
                translation: t
              })}
            </List>
          ))}
          <div style={{flex: 1}}></div>
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return <>
    { 
      mobileDevice ?
      <Drawer
        anchor="left"
        classes={{ paper: cx(classes.mobileDrawer, classes.metalMenu) }}
        onClose={onMobileClose}
        open={openMobile}
        variant="temporary"
      >
        {content}
      </Drawer> :
      <Drawer
        anchor="left"
        classes={{ paper: cx(classes.desktopDrawer, classes.metalMenu) }}
        open
        variant="persistent"
      >
        {content}
      </Drawer>
    }
  </>;
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
